import { useToast } from '@chakra-ui/react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { keyBy, snakeCase, sortBy, uniqBy } from 'lodash';

import {
    AssetApi,
    AssetClass,
    AssetReplacement,
    AssetReplacementFromJSON,
    BondCreateRequest,
    Configuration,
    EquityCreateRequest,
    FilterType,
    FuturesCreateRequest,
    GetEnrichedAssetRequest,
    OptionsCreateRequest,
    SearchAssetsRequest,
} from '../_openapi/portfolio-optimizer';
import { processFilter } from '../widgets/filters/filterUtils';
import { defaultApiConfig } from './defaultApiConfig';

const assetApi = new AssetApi(new Configuration(defaultApiConfig));

export const useAssets = ({
    pagination,
    sorting,
    columnFilters,
}: {
    pagination: PaginationState;
    sorting?: SortingState;
    columnFilters?: ColumnFiltersState;
}) =>
    useQuery({
        queryKey: ['asset', 'list', { pagination, sorting, columnFilters }],
        queryFn: () => {
            const query: GetEnrichedAssetRequest = {
                ...pagination,
            };

            if (sorting?.length) {
                query.sortLabel = snakeCase(sorting[0].id);
                query.sortDesc = sorting[0].desc;
            }

            if (columnFilters?.length) {
                query.filter = columnFilters.map(processFilter);
            }

            return assetApi.getEnrichedAsset(query);
        },
        placeholderData: keepPreviousData,
    });

export const useAsset = ({ sId }: { sId?: string }) =>
    useQuery({
        queryKey: ['asset', 'details', sId],
        queryFn: async () => {
            const page = await assetApi.getAsset({
                pageIndex: 0,
                pageSize: 1,
                filter: [
                    {
                        type: 'string',
                        field: 's_id',
                        value: sId,
                    },
                ],
            });
            return page.assets[0];
        },
        enabled: !!sId,
    });

export const useAssetLookup = (assetIdentifier?: string) =>
    useQuery({
        queryKey: ['asset', 'lookup', assetIdentifier],
        queryFn: ({ signal }) => {
            return lookupAsset(assetIdentifier!, signal);
        },
        enabled: !!assetIdentifier,
    });

export const lookupAsset = async (assetIdentifier: string, signal?: AbortSignal) => {
    const response = await assetApi.getAsset(
        {
            pageIndex: 0,
            pageSize: 1500,
            filter: [
                {
                    type: 'list',
                    field: '',
                    value: [
                        {
                            type: FilterType.STRING,
                            field: 'symbol',
                            value: assetIdentifier,
                        },
                        {
                            type: FilterType.STRING,
                            field: 'cusip',
                            value: assetIdentifier,
                        },
                        {
                            type: FilterType.STRING,
                            field: 's_id',
                            value: assetIdentifier,
                        },
                    ],
                },
            ],
        },
        { signal },
    );

    return sortBy(uniqBy(response.assets, 'sId'), 'symbol');
};

export const useAssetsBySIds = (sIds: string[]) => {
    const sortedSIds = [...new Set(sIds)].sort();

    return useQuery({
        queryKey: ['asset', 'bySIds', sortedSIds],
        queryFn: async () => {
            const page = await assetApi.getAsset({
                pageIndex: 0,
                pageSize: sortedSIds.length + 50,
                filter: [
                    {
                        type: 'list',
                        field: '',
                        value: sortedSIds.map((sId) => ({
                            type: FilterType.STRING,
                            field: 's_id',
                            value: sId,
                        })),
                    },
                ],
            });
            return keyBy(uniqBy(page.assets, 'sId'), 'sId');
        },
        enabled: sortedSIds.length > 0,
    });
};

export const useSearchAssets = (searchTerm?: string, limit?: number, assetClass?: AssetClass) =>
    useQuery({
        queryKey: ['asset', 'search', searchTerm, limit, assetClass],
        queryFn: ({ signal }) => {
            return searchAssets(searchTerm!, limit, assetClass, signal);
        },
        enabled: !!searchTerm,
    });

export const useAssetReplacements = (scIds: string[]) => {
    const sortedScIds = [...new Set(scIds)].sort();

    return useQuery({
        queryKey: ['asset', 'replacements', sortedScIds],
        queryFn: async () => {
            const response = await assetApi.getAssetReplacements({
                requestBody: sortedScIds as unknown as Set<string>,
            });
            return normalizeAssetReplacements(response);
        },
        enabled: sortedScIds.length > 0,
    });
};

const normalizeAssetReplacements = (response: Record<string, unknown[]>): Record<string, AssetReplacement[]> =>
    Object.fromEntries(
        Object.entries(response).map(([scId, replacements]) => [
            scId,
            replacements.map(AssetReplacementFromJSON).filter(hasSecurityId),
        ]),
    );

const hasSecurityId = (replacement: AssetReplacement): boolean => !!replacement.sId;

export const searchAssets = async (
    searchTerm: string,
    limit?: number,
    assetClass?: AssetClass,
    signal?: AbortSignal,
) => {
    const params: SearchAssetsRequest = {
        searchTerm,
    };

    if (limit !== undefined) {
        params.limit = limit;
    }

    if (assetClass !== undefined) {
        params.assetClass = assetClass;
    }

    return assetApi.searchAssets(params, { signal });
};

export const useCreateAsset = () => {
    const toast = useToast();

    return useMutation({
        mutationFn: ({
            assetData,
        }: {
            assetData: BondCreateRequest | EquityCreateRequest | FuturesCreateRequest | OptionsCreateRequest;
        }) => {
            switch (assetData.assetClass) {
                case 'Bond':
                    return assetApi.saveNewBond({ bondCreateRequest: assetData as BondCreateRequest });
                case 'Equity':
                    return assetApi.saveNewEquity({ equityCreateRequest: assetData as EquityCreateRequest });
                case 'Options':
                    return assetApi.saveNewFutures({ futuresCreateRequest: assetData as FuturesCreateRequest });
                case 'Futures':
                    return assetApi.saveNewOptions({ optionsCreateRequest: assetData as OptionsCreateRequest });
                default:
                    throw new Error('Unknown asset class');
            }
        },
        onSuccess: () => {
            toast({
                title: 'Asset saved',
                description: 'Changes might take a couple of minutes to show up',
                status: 'success',
                duration: 3000,
            });
        },
    });
};
